/* eslint-disable */
/**
 * OnlineBedStore — Add to Basket state helper (v3, conservative).
 *
 * Goal: when the HulkApps Product Options app has disabled the Add to Basket
 * button because a required option (e.g. fabric) hasn't been picked, repaint
 * the button grey, change the label to "SELECT <FIELD> ↑", and on click
 * smooth-scroll the user to the missing option.
 *
 * Important guard rails:
 *  - We only override the button when we can pinpoint a SPECIFIC missing
 *    option. If we can't, we leave HulkApps's button untouched. This avoids
 *    the dead-end "Select your options" state where the label couldn't
 *    update because we had no element to drive label changes from.
 *  - No body-wide MutationObservers (caused a feedback loop with HulkApps
 *    in a previous iteration).
 *  - Every textContent write is idempotent.
 *  - Sold-out / unavailable states are left alone.
 *  - HulkApps swatches sometimes don't fire native `change` events on hidden
 *    radios — we listen to `click` inside the options block as well, with a
 *    tiny defer so HulkApps can update its own DOM first.
 */
(function () {
  'use strict';

  var INIT = 'data-obs-atb-init';
  var ACTIVE = 'obs-atb-incomplete';

  function getHeaderOffset() {
    var h = document.querySelector(
      '.shopify-section-group-header-group, header.cc-header, header[role="banner"], header'
    );
    return h ? h.getBoundingClientRect().height + 16 : 80;
  }

  function smoothScrollTo(el) {
    if (!el || !el.getBoundingClientRect) return;
    var rect = el.getBoundingClientRect();
    var top = rect.top + window.pageYOffset - getHeaderOffset();
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function isFilled(option) {
    var inputs = option.querySelectorAll(
      'input[type="radio"], input[type="checkbox"], input[type="text"], input[type="number"], input[type="email"], input[type="tel"], textarea, select'
    );
    if (inputs.length === 0) return true;
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      var t = (inp.type || inp.tagName || '').toLowerCase();
      if (t === 'radio' || t === 'checkbox') {
        if (inp.checked) return true;
      } else {
        if (inp.value && String(inp.value).trim()) return true;
      }
    }
    return false;
  }

  function isRequired(option) {
    if (!option) return false;
    if (option.querySelector('input[required], select[required], textarea[required]')) return true;
    if (option.dataset && (option.dataset.required === 'true' || option.dataset.required === '1')) return true;
    if (option.className && /\brequired\b/i.test(option.className)) return true;
    var label = option.querySelector(
      '.hulkapps_option_label, .hulkapps_option > label, label, legend, .label'
    );
    if (label) {
      if (/\*/.test(label.textContent || '')) return true;
      if (label.className && /\brequired\b/i.test(label.className)) return true;
    }
    return false;
  }

  function getOptionLabel(option) {
    var label = option.querySelector(
      '.hulkapps_option_label, .hulkapps_option > label, label, legend, .label'
    );
    var raw = label ? (label.textContent || '') : '';
    return raw.replace('*', '').replace(/\s+/g, ' ').trim();
  }

  function findMissingOption() {
    try {
      var hulkOptions = document.querySelectorAll('.hulkapps_option');

      // Pass 1: explicitly required + unfilled.
      for (var i = 0; i < hulkOptions.length; i++) {
        if (isRequired(hulkOptions[i]) && !isFilled(hulkOptions[i])) {
          return { el: hulkOptions[i], label: getOptionLabel(hulkOptions[i]) || 'options' };
        }
      }

      // Pass 2: if NO option appears required (detection failure), look for
      // any unfilled HulkApps option. Better than nothing.
      var anyRequired = false;
      for (var k = 0; k < hulkOptions.length; k++) {
        if (isRequired(hulkOptions[k])) { anyRequired = true; break; }
      }
      if (!anyRequired) {
        for (var m = 0; m < hulkOptions.length; m++) {
          if (!isFilled(hulkOptions[m])) {
            return { el: hulkOptions[m], label: getOptionLabel(hulkOptions[m]) || 'options' };
          }
        }
      }

      // Pass 3: native Shopify variant pickers.
      var native = document.querySelectorAll('.option-selector');
      for (var n = 0; n < native.length; n++) {
        var inputs = native[n].querySelectorAll('input[type="radio"]');
        if (inputs.length === 0) continue;
        var any = false;
        for (var p = 0; p < inputs.length; p++) {
          if (inputs[p].checked) { any = true; break; }
        }
        if (!any) return { el: native[n], label: getOptionLabel(native[n]) || 'options' };
      }
    } catch (err) { /* swallow */ }
    return null;
  }

  function isGenuineSoldOut(btn) {
    var t = (btn.textContent || '').trim().toLowerCase();
    return t.indexOf('sold out') !== -1 || t.indexOf('unavailable') !== -1;
  }

  // Idempotent writes — only mutates DOM when something changes.
  function setText(btn, value) {
    if (btn.textContent.trim() !== value) btn.textContent = value;
  }

  function refresh(btn) {
    if (!btn || isGenuineSoldOut(btn)) return;

    if (!btn.dataset.obsOriginalText) {
      btn.dataset.obsOriginalText = btn.getAttribute('data-add-to-cart-text') || btn.textContent.trim() || 'Add to Basket';
    }

    var disabled = btn.hasAttribute('disabled');
    var active = btn.classList.contains(ACTIVE);

    // Case 1: enabled and not in our active state — nothing to do.
    if (!disabled && !active) return;

    // Case 2: enabled but we previously activated — restore original.
    if (!disabled && active) {
      btn.classList.remove(ACTIVE);
      btn.removeAttribute('aria-disabled');
      setText(btn, btn.dataset.obsOriginalText);
      return;
    }

    // Case 3: disabled. Try to identify a specific missing option.
    var missing = findMissingOption();

    if (!missing) {
      // No missing option we can name. Either HulkApps hasn't injected its
      // options yet, or all detection paths failed. Don't override the
      // button — leave the user with whatever HulkApps put there. If we'd
      // already activated previously, walk that back so we don't get stuck
      // in a stale "SELECT ..." label.
      if (active) {
        btn.classList.remove(ACTIVE);
        btn.removeAttribute('aria-disabled');
        setText(btn, btn.dataset.obsOriginalText);
      }
      return;
    }

    // Case 4: disabled and we know what's missing — override the button.
    btn.removeAttribute('disabled');
    if (!active) btn.classList.add(ACTIVE);
    btn.setAttribute('aria-disabled', 'true');
    setText(btn, ('Select ' + missing.label).toUpperCase());
  }

  function attach(btn) {
    if (btn.getAttribute(INIT)) return;
    btn.setAttribute(INIT, '1');

    btn.addEventListener('click', function (e) {
      if (!btn.classList.contains(ACTIVE)) return;
      e.preventDefault();
      e.stopPropagation();
      var missing = findMissingOption();
      var target = missing ? missing.el : document.querySelector('.hulkapps_option, .option-selector');
      if (target) {
        smoothScrollTo(target);
        target.classList.add('obs-atb-flash');
        setTimeout(function () { target.classList.remove('obs-atb-flash'); }, 1600);
      }
    }, true);

    // Narrow-scope: just this button's disabled attribute.
    var observer = new MutationObserver(function () { refresh(btn); });
    observer.observe(btn, { attributes: true, attributeFilter: ['disabled'] });

    refresh(btn);
  }

  function findAndAttach() {
    var btns = document.querySelectorAll('button[name="add"]');
    for (var i = 0; i < btns.length; i++) attach(btns[i]);
  }

  function refreshKnownButtons() {
    var btns = document.querySelectorAll('button[name="add"][' + INIT + '="1"]');
    for (var i = 0; i < btns.length; i++) refresh(btns[i]);
  }

  function maybeChangedHandler(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest('.hulkapps_option, .option-selector, [id^="hulkapps_custom_options_"]')) return;
    // HulkApps may update its own state on the next tick — defer briefly.
    setTimeout(refreshKnownButtons, 80);
  }

  function start() {
    findAndAttach();
    document.addEventListener('change', maybeChangedHandler, true);
    document.addEventListener('input', maybeChangedHandler, true);
    document.addEventListener('click', maybeChangedHandler, true);

    var retries = 0;
    var interval = setInterval(function () {
      findAndAttach();
      refreshKnownButtons();
      if (++retries >= 12) clearInterval(interval); // ~9s ceiling
    }, 750);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
